import { z } from 'zod'
import { actionTypeIdSchema } from './actions/types'
import { DIMENSION_SET_KEYS, GSC_SEARCH_TYPES } from './gsc/sync-plan'
import { OUTREACH_MAX_SEQUENCE } from './outreach/plan'
import { SCRAPE_MAX_AGE_HOURS, scrapeUrlSchema } from './scrape/plan'
import { serpKeywordSchema, serpMarketKey, serpMarketSchema } from './serp/market'
import { SERP_DEFAULT_PAGES, SERP_MAX_PAGES } from './serp/plan'

/**
 * The queue contract, shared by the api (which enqueues) and the worker (which
 * consumes). Keeping the payload schemas here makes a mismatch between the two
 * a typecheck failure rather than a job that parses wrong at 3am.
 */
export const JOB_NAMES = {
  discoverProperties: 'gsc.discover-properties',
  probeSearchTypes: 'gsc.probe-search-types',
  backfillSite: 'gsc.backfill-site',
  syncWindow: 'gsc.sync-window',
  syncSiteDaily: 'gsc.sync-site-daily',
  refreshSitemaps: 'gsc.refresh-sitemaps',
  inspectUrls: 'gsc.inspect-urls',
  discoverBingSites: 'bing.discover-sites',
  syncBingSite: 'bing.sync-site',
  discoverSitemaps: 'sitemap.discover',
  fetchSitemap: 'sitemap.fetch',
  backfillBacklinks: 'backlinks.backfill-site',
  syncBacklinks: 'backlinks.sync-site',
  syncBacklinkLinks: 'backlinks.sync-links',
  backfillCompetitor: 'backlinks.backfill-competitor',
  syncCompetitor: 'backlinks.sync-competitor',
  checkSerp: 'serp.check',
  runPrompt: 'ai.run-prompt',
  runPromptBatch: 'ai.run-prompt-batch',
  scrapeUrl: 'scrape.fetch',
  scanActions: 'actions.scan-site',
  enrichAction: 'actions.enrich',
  reviewActions: 'actions.review-site',
  dispatchActionsDaily: 'actions.dispatch-daily',
  dispatchActionReviewDaily: 'actions.dispatch-review-daily',
  sourceOutreach: 'outreach.source-site',
  dispatchOutreachSource: 'outreach.dispatch-source',
  fillOutreach: 'outreach.fill-site',
  researchProspect: 'outreach.research-prospect',
  draftOutreachMessage: 'outreach.draft-message',
  tickOutreach: 'outreach.tick-site',
  sendOutreachMessage: 'outreach.send-message',
  dispatchOutreachTick: 'outreach.dispatch-tick',
  reviewOutreach: 'outreach.review-site',
  dispatchOutreachReview: 'outreach.dispatch-review',
} as const

export type JobName = (typeof JOB_NAMES)[keyof typeof JOB_NAMES]

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
const dimensionSet = z.enum(DIMENSION_SET_KEYS)
const searchType = z.enum(GSC_SEARCH_TYPES)

export const discoverPropertiesPayload = z.object({
  connectionId: z.uuid(),
  /** Set on the post-OAuth run so the picker can wait on a known job. */
  initial: z.boolean().default(false),
})

/** Re-reads a Bing key's site list and links any site whose host matches. */
export const discoverBingSitesPayload = z.object({
  connectionId: z.uuid(),
})

/** Bing hands back its whole history on every call, so one job is both the
 * backfill and the nightly refresh. */
export const syncBingSitePayload = z.object({
  siteId: z.uuid(),
})

export const probeSearchTypesPayload = z.object({
  siteId: z.uuid(),
  propertyId: z.uuid(),
})

export const backfillSitePayload = z.object({
  siteId: z.uuid(),
  propertyId: z.uuid(),
})

/**
 * One unit of Search Console work: a single series, for a single corpus, over a
 * date span that is one day for everything except the totals series. Idempotent
 * by construction — every write is an upsert on the natural key — so a retry
 * after a partial failure is always safe.
 */
export const syncWindowPayload = z.object({
  siteId: z.uuid(),
  propertyId: z.uuid(),
  dimensionSet,
  searchType,
  startDate: isoDate,
  endDate: isoDate,
})

export const syncSiteDailyPayload = z.object({
  siteId: z.uuid(),
})

export const refreshSitemapsPayload = z.object({
  siteId: z.uuid(),
  propertyId: z.uuid(),
})

/**
 * Inspection is always asked for by name, never chosen by the job.
 *
 * Google's ceiling is about 2,000 URLs a day per property, and a page that
 * takes impressions has already proved it is indexed — so a nightly rotation
 * spent that budget re-confirming what the traffic series says for free. It
 * runs only when someone opens a page and asks why.
 */
export const inspectUrlsPayload = z.object({
  siteId: z.uuid(),
  propertyId: z.uuid(),
  pageIds: z.array(z.number().int().positive()).min(1).max(25),
})

/**
 * Runs for every site, linked to Search Console or not — the domain is all it
 * needs. `force` skips the conditional GET on robots.txt so a user asking for a
 * re-check gets one instead of a 304.
 */
export const discoverSitemapsPayload = z.object({
  siteId: z.uuid(),
  force: z.boolean().default(false),
})

/**
 * One sitemap document. A sitemap index enqueues one of these per child rather
 * than recursing inline, so a 50,000 entry index is 50,000 short jobs instead
 * of one job held open for an hour.
 */
export const fetchSitemapPayload = z.object({
  siteId: z.uuid(),
  sitemapId: z.uuid(),
  /** Guards against an index that lists itself, which is not rare. */
  depth: z.number().int().min(0).max(3).default(0),
  force: z.boolean().default(false),
})

/**
 * The first pull for a site: the whole monthly history in one request, then as
 * much of the current referring-domain set as the budget allows. Separate from
 * the weekly sync because that one asks only for what changed, and "what
 * changed since never" is the entire profile.
 */
export const backfillBacklinksPayload = z.object({
  siteId: z.uuid(),
})

/**
 * The weekly pass. Runs the stages in one job rather than fanning them out
 * because they share a single per-site budget, and two stages spending it
 * concurrently would each believe they could afford the other's rows.
 *
 * `moved` is what the estate-wide new/lost probe found. False means the
 * provider reports no change for this site, so the run takes the topline and
 * stops instead of paying to re-read a set we already have.
 */
/**
 * The identity of one queued backfill.
 *
 * Here rather than in either app because both enqueue it: the api the moment a
 * site is added, and the weekly pass when it finds a site with nothing stored.
 * A backfill is the one pull that spends a whole monthly ceiling, and
 * `BacklinkBudget` reads the ledger once per run — so two of them for one site
 * would each believe the full month was theirs and between them spend twice it.
 */
export function backlinkBackfillKey(siteId: string): string {
  return `backlinks-backfill:${siteId}`
}

export const syncBacklinksPayload = z.object({
  siteId: z.uuid(),
  moved: z.boolean().default(true),
  /** Skips both the change probe and the throttle, for a refresh someone asked
   * for by name. */
  force: z.boolean().default(false),
})

/**
 * The whole link graph, which only runs where a site has opted into link-level
 * data. Its own job because a large profile is hundreds of paginated requests
 * and holding a worker slot open for all of them would starve every other site.
 */
export const syncBacklinkLinksPayload = z.object({
  siteId: z.uuid(),
  /** 'broken' is the slice every site gets: filtered to dead targets, which is
   * a small fraction of any profile and therefore affordable by default. */
  scope: z.enum(['broken', 'all']).default('broken'),
  mode: z.enum(['delta', 'full']).default('delta'),
})

/**
 * The first pull for a competitor: their whole monthly history, then as much of
 * their referring-domain set as the site's remaining budget allows, then one
 * example link for the strongest of those domains.
 *
 * `siteId` rides along because the competitor's spend is the site's — the site
 * is the billing unit and a competitor is a thing that site asked for — and
 * `BacklinkBudget` needs the site to read the ledger against.
 */
export const backfillCompetitorPayload = z.object({
  competitorId: z.uuid(),
})

/**
 * The weekly pass over one competitor, and the same bargain as the site's: the
 * topline always, the domain set only when the estate-wide new/lost probe says
 * their profile moved. A competitor whose links have not changed costs nothing
 * beyond a share of one probe request.
 */
export const syncCompetitorPayload = z.object({
  competitorId: z.uuid(),
  moved: z.boolean().default(true),
  force: z.boolean().default(false),
})

/**
 * One keyword, one market, one moment.
 *
 * The payload carries the keyword as text rather than an id because the check is
 * not a fact about a tenant: the worker interns the keyword and the market into
 * the shared tables, and the same job serves whoever asked for it. `siteId` is
 * only ever attribution — which site's screen the request came from — and the
 * result it stores is identical without it.
 */
export const checkSerpPayload = z.object({
  keyword: serpKeywordSchema,
  market: serpMarketSchema,
  pages: z.number().int().min(1).max(SERP_MAX_PAGES).default(SERP_DEFAULT_PAGES),
  siteId: z.uuid().optional(),
  /** Spends a credit on a keyword the cache could already answer. Only ever set
   * by someone asking for it by name, never by the schedule. */
  force: z.boolean().default(false),
})

/**
 * The identity of one queued check, and the whole cross-tenant collapse.
 *
 * `serp.check` runs the 'short' policy, so two workspaces pressing the button on
 * the same keyword in the same minute become one paid request — but only if both
 * enqueues build the same key, and one of them comes from the api while the other
 * comes from the nightly fan-out. That is why this lives beside the payload
 * rather than in either app.
 *
 * Depth is part of the key because a one-page check cannot answer a three-page
 * question, and `force` is part of it because someone who asked for a fresh
 * reading must not be handed a pending cached one instead.
 */
export function serpCheckKey(input: CheckSerpPayload): string {
  return [
    'serp',
    serpMarketKey(input.market),
    input.pages,
    input.force ? 'force' : 'cache',
    input.keyword,
  ].join(':')
}

/**
 * A model call, addressed by nothing but the run it is to perform.
 *
 * The `ai_run` row is written first, by whoever wants the answer, and this job
 * is handed its id. That is what lets the api hand back something to poll — and
 * it is the retry guard, because a redelivery finds a run that has already
 * succeeded and stops, where a payload-carrying job would pay twice for the
 * same answer.
 */
export const runPromptPayload = z.object({
  runId: z.uuid(),
})

/**
 * One page, read once, for whoever asked.
 *
 * Like `serp.check` and unlike everything else on this list, the payload carries
 * no tenant. A page's content is a fact about the internet at a moment, not a
 * fact about a workspace, so two organizations pointing at the same competitor
 * are asking one question and must pay for one answer. `siteId` is attribution
 * only — whose screen the request came from — and the row this job writes is
 * byte-identical without it.
 *
 * `maxAgeHours` is how the caller says what it can live with. A report willing
 * to read last week's copy costs nothing; one that needs today's says so and is
 * billed for it. Absent means `SCRAPE_CACHE_TTL_HOURS`.
 */
export const scrapeUrlPayload = z.object({
  url: scrapeUrlSchema,
  siteId: z.uuid().optional(),
  maxAgeHours: z.number().int().min(0).max(SCRAPE_MAX_AGE_HOURS).optional(),
  /** Refuses both caches — ours and the reader's. Only ever set by someone
   * asking for the page as it is now, never by a schedule. */
  force: z.boolean().default(false),
})

/**
 * One pass of every detector over one site.
 *
 * The unit is the site rather than the detector, because reconciliation is what
 * this job does and reconciliation is a statement about the whole set: a
 * detector that returns nothing tonight is how an action goes stale, and a
 * per-detector job could never tell "found nothing" from "did not run".
 *
 * `types` narrows it for a re-run from /admin — one check re-scanned after its
 * threshold was argued about — and absent means every detector whose feeds have
 * something to say.
 */
export const scanActionsPayload = z.object({
  siteId: z.uuid(),
  types: z.array(actionTypeIdSchema).optional(),
  /** Skips the enrichment fan-out. What a dry run is for: see what the
   * thresholds produce without paying a model to describe it. */
  skipEnrichment: z.boolean().default(false),
})

/**
 * One action, described.
 *
 * The only part of this feed that spends, so it is its own job with its own
 * queue rather than a step inside the scan: a model that is down, rate limited
 * or out of budget must leave the scan's reconciliation intact. The action row
 * already exists and already stands up when this runs — everything here is
 * decoration over evidence that was written before it.
 */
export const enrichActionPayload = z.object({
  actionId: z.uuid(),
  /** Re-describes an action whose evidence has not moved. Only ever set by
   * hand from /admin, because the whole point of the evidence hash is that a
   * nightly scan does not do this. */
  force: z.boolean().default(false),
})

/**
 * Every site at once.
 *
 * Normally a cron's, and deliberately so — a fan-out carries no site because it
 * is the thing that decides which sites there are. It is in the shared contract
 * rather than the worker's own list because `/admin` needs it: "re-run the
 * checks everywhere" is a deployment-wide question, and the alternative is the
 * api looping the site table and inventing a second idea of which sites exist.
 *
 * `force` is what makes it usable by hand. The scheduled fan-out throttles each
 * site for twenty hours so a cron that fires twice does the work once, and that
 * same throttle would silently refuse a run somebody asked for an hour later.
 */
export const dispatchActionsPayload = z.object({
  force: z.boolean().default(false),
})

/**
 * Did it work.
 *
 * Per site rather than per action for the same reason the scan is: the control
 * this measures each outcome against is the rest of the site over the same
 * days, and reading that once per action would be one scan per row for a number
 * every row shares.
 */
export const reviewActionsPayload = z.object({
  siteId: z.uuid(),
})

/**
 * One pass of a site's sources — its dead links, its mentions, its competitors'
 * links and its target sweep — written as candidates, then the research queue
 * topped up.
 *
 * `harvest` is the second pass. A sweep that had to enqueue searches brings
 * itself back once a few minutes later to read what landed; the flag is what
 * stops it bringing itself back forever.
 */
export const sourceOutreachPayload = z.object({
  siteId: z.uuid(),
  force: z.boolean().default(false),
  harvest: z.boolean().default(false),
})

export const dispatchOutreachSourcePayload = z.object({
  force: z.boolean().default(false),
})

/**
 * Pull research up to the number of drafts a person should have waiting. Sent
 * after sourcing and after every decision, so a dismissed draft is replaced
 * rather than leaving the day short.
 */
export const fillOutreachPayload = z.object({
  siteId: z.uuid(),
})

/**
 * One candidate, read and judged: their pages scraped, the gates applied, and
 * the one model call that decides and writes. `instruction` is a person asking
 * for a redraft in their own words.
 */
export const researchProspectPayload = z.object({
  prospectId: z.uuid(),
  /** A person asked for this draft to be written again; the api has already
   * cancelled the old one and moved the row to `researching`. */
  redraft: z.boolean().default(false),
  instruction: z.string().max(500).optional(),
})

/** A follow-up, written against the silence after the opener. */
export const draftOutreachMessagePayload = z.object({
  prospectId: z.uuid(),
  sequence: z.number().int().min(1).max(OUTREACH_MAX_SEQUENCE),
})

/**
 * Advance this site's pipeline: read the inbox, schedule the follow-ups that
 * have come due, send what is due to go.
 *
 * One job rather than three because they are one question asked of one site,
 * they share a cadence, and every one of them has to respect the same pause and
 * the same daily cap. Three jobs would be three cron entries that can disagree
 * about whether the site is sending — a poller that keeps running while the
 * sender is paused is how a bounce rate over the ceiling still produces a
 * scheduled follow-up.
 */
export const tickOutreachPayload = z.object({
  siteId: z.uuid(),
})

export const dispatchOutreachTickPayload = z.object({})

/** One approved mail, now, because a person pressed send. Everything the tick
 * checks still binds except the sending hours — the person is the hours. */
export const sendOutreachMessagePayload = z.object({
  messageId: z.uuid(),
})

/**
 * Did they link. Per site rather than per prospect because the answer is a read
 * of the site's own referring domains over the attribution window, and asking
 * it once per prospect would be one scan per row for a set every row shares.
 */
export const reviewOutreachPayload = z.object({
  siteId: z.uuid(),
})

export const dispatchOutreachReviewPayload = z.object({})

/**
 * The identity of one queued scrape, and the other half of the cross-tenant
 * collapse.
 *
 * The stored copy collapses requests that are minutes apart; this collapses the
 * ones that are milliseconds apart, before anything has landed for the cache to
 * find. Both halves are needed and neither is sufficient, which is why the key
 * is built here rather than in whichever app happens to be enqueuing.
 *
 * `maxAgeHours` is deliberately *not* in the key. Two callers wanting the same
 * URL at different freshnesses still want the same fetch, and the stricter one
 * is satisfied by it — where folding it in would split them into two paid reads
 * of the same page. `force` is in the key, because someone who asked for a fresh
 * copy must not be handed a pending cached one instead.
 */
export function scrapeKey(input: ScrapeUrlPayload): string {
  return ['scrape', input.force ? 'force' : 'cache', input.url].join(':')
}

export type DiscoverPropertiesPayload = z.infer<typeof discoverPropertiesPayload>
export type DiscoverBingSitesPayload = z.infer<typeof discoverBingSitesPayload>
export type SyncBingSitePayload = z.infer<typeof syncBingSitePayload>
export type ProbeSearchTypesPayload = z.infer<typeof probeSearchTypesPayload>
export type BackfillSitePayload = z.infer<typeof backfillSitePayload>
export type SyncWindowPayload = z.infer<typeof syncWindowPayload>
export type SyncSiteDailyPayload = z.infer<typeof syncSiteDailyPayload>
export type RefreshSitemapsPayload = z.infer<typeof refreshSitemapsPayload>
export type InspectUrlsPayload = z.infer<typeof inspectUrlsPayload>
export type DiscoverSitemapsPayload = z.infer<typeof discoverSitemapsPayload>
export type FetchSitemapPayload = z.infer<typeof fetchSitemapPayload>
export type BackfillBacklinksPayload = z.infer<typeof backfillBacklinksPayload>
export type SyncBacklinksPayload = z.infer<typeof syncBacklinksPayload>
export type SyncBacklinkLinksPayload = z.infer<typeof syncBacklinkLinksPayload>
export type BackfillCompetitorPayload = z.infer<typeof backfillCompetitorPayload>
export type SyncCompetitorPayload = z.infer<typeof syncCompetitorPayload>
export type CheckSerpPayload = z.infer<typeof checkSerpPayload>
export type ScrapeUrlPayload = z.infer<typeof scrapeUrlPayload>
export type RunPromptPayload = z.infer<typeof runPromptPayload>
export type ScanActionsPayload = z.infer<typeof scanActionsPayload>
export type EnrichActionPayload = z.infer<typeof enrichActionPayload>
export type ReviewActionsPayload = z.infer<typeof reviewActionsPayload>
export type DispatchActionsPayload = z.infer<typeof dispatchActionsPayload>
export type SourceOutreachPayload = z.infer<typeof sourceOutreachPayload>
export type DispatchOutreachSourcePayload = z.infer<typeof dispatchOutreachSourcePayload>
export type FillOutreachPayload = z.infer<typeof fillOutreachPayload>
export type ResearchProspectPayload = z.infer<typeof researchProspectPayload>
export type DraftOutreachMessagePayload = z.infer<typeof draftOutreachMessagePayload>
export type TickOutreachPayload = z.infer<typeof tickOutreachPayload>
export type SendOutreachMessagePayload = z.infer<typeof sendOutreachMessagePayload>
export type DispatchOutreachTickPayload = z.infer<typeof dispatchOutreachTickPayload>
export type ReviewOutreachPayload = z.infer<typeof reviewOutreachPayload>
export type DispatchOutreachReviewPayload = z.infer<typeof dispatchOutreachReviewPayload>
